/**
 * PowerNumberEntry Component
 * Elegant tag-input for entering power numbers to discharge.
 * Accepts comma, space, tab, newline, or Enter-separated values.
 * Supports paste from spreadsheets, bulk entry, and auto-complete via debounced API search.
 *
 * Enhanced with inline validation status, proof upload, and reason/comments.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  XMarkIcon,
  HashtagIcon,
  ClipboardDocumentIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import { OathButton, OathSpinner, OathInput } from '../../../shared/OathUI';
import { OathPagination, PAGE_SIZE_ALL } from '../../../shared/OathTableComponents';
import Tooltip from '@mui/material/Tooltip';
import { searchEligiblePowers } from '../../../../services/oathDischargeService';
import { formatCurrencyWithSymbol } from '../utils/formatters';
import { VALIDATION_COLORS, VALIDATION_STATUS_LABELS } from '../utils/constants';
import ElectronicPowerBadge from '../../../shared/ElectronicPowerBadge';

const MIN_SEARCH_LENGTH = 3;
const MAX_SUGGESTIONS = 6;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500, PAGE_SIZE_ALL];

const STATUS_LABELS = {
  E: 'Executed',
  F: 'Forfeiture',
  J: 'Judgment',
  T: 'Transfer',
  R: 'Reinstate',
  S: 'Set-Aside',
  X: 'Expired',
};

// ============================================================================
// Validation status chip — shows Ready / Proof Required / Blocked inline
// ============================================================================

const ValidationChip = ({ validation, powerStatus, statusName }) => {
  if (!validation) {
    // No client validation yet — show the power's raw status code as fallback
    if (powerStatus) {
      const label = statusName || STATUS_LABELS[powerStatus] || powerStatus;
      return (
        <span
          className="inline-flex items-center px-2 py-0.5 qhd:px-2.5 qhd:py-1 text-xs qhd:text-sm font-semibold rounded-full whitespace-nowrap bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
          title={label}
        >
          {label}
        </span>
      );
    }
    return <span className="text-gray-400 dark:text-gray-500">&mdash;</span>;
  }

  const { status, reason } = validation;
  const label = VALIDATION_STATUS_LABELS[status] || status;

  if (reason && (status === 'needsProof' || status === 'blocked')) {
    const borderClass = status === 'needsProof'
      ? 'border-orange-200 dark:border-orange-800/60 bg-orange-50/50 dark:bg-orange-900/10'
      : 'border-red-200 dark:border-red-800/60 bg-red-50/50 dark:bg-red-900/10';
    const dotClass = status === 'needsProof'
      ? 'bg-orange-400 dark:bg-orange-500'
      : 'bg-red-400 dark:bg-red-500';
    const labelClass = status === 'needsProof'
      ? 'text-orange-700 dark:text-orange-400'
      : 'text-red-700 dark:text-red-400';
    const reasonClass = status === 'needsProof'
      ? 'text-orange-600/80 dark:text-orange-400/70'
      : 'text-red-600/80 dark:text-red-400/70';

    return (
      <div className={`inline-flex flex-col gap-0.5 px-2.5 py-1.5 qhd:px-3 qhd:py-2 rounded-lg border ${borderClass}`}>
        <span className={`flex items-center gap-1.5 text-xs qhd:text-sm font-semibold ${labelClass}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${dotClass} shrink-0`} />
          {label}
        </span>
        <span className={`text-[11px] sm:text-xs qhd:text-[11px] leading-snug break-words ${reasonClass} pl-3`}>
          {reason}
        </span>
      </div>
    );
  }

  const colorClass = VALIDATION_COLORS[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 qhd:px-2.5 qhd:py-1 text-xs qhd:text-sm font-semibold rounded-full whitespace-nowrap ${colorClass}`}
      title={reason || label}
    >
      {label}
    </span>
  );
};

// ============================================================================
// Proof cell — shows upload button or proof count
// ============================================================================

const ProofCell = ({ powerNo, validation, proofData, hasServerProof, onUploadProof }) => {
  if (!validation || validation.status === 'blocked') {
    return <span className="text-gray-400 dark:text-gray-500">&mdash;</span>;
  }

  const hasProof = !!proofData?.fileId || hasServerProof;

  if (hasProof) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs qhd:text-sm font-medium text-green-700 dark:text-green-400">
          <CheckCircleIcon className="h-3.5 w-3.5 qhd:h-4 qhd:w-4" />
          Uploaded
        </span>
        <OathButton variant="outline" size="sm" onClick={() => onUploadProof(powerNo)}>
          View
        </OathButton>
      </div>
    );
  }

  return (
    <OathButton variant="outline" size="sm" onClick={() => onUploadProof(powerNo)}>
      <ArrowUpTrayIcon className="h-3.5 w-3.5 qhd:h-4 qhd:w-4 mr-1" />
      Upload
    </OathButton>
  );
};

// ============================================================================
// Selected power row — shows power number, defendant name, bond amount,
// validation status, proof, and remove button
// ============================================================================

const ShimmerBar = ({ className = '' }) => (
  <div className={`h-4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`} />
);

const SelectedPowerRow = ({ powerNo, details, validation, proofData, onRemove, onUploadProof, isEven, isSaving, agencyId }) => (
  <tr className={`hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors ${isEven ? 'bg-gray-50/50 dark:bg-gray-800/50' : ''}`}>
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <span className="inline-flex items-center gap-1.5">
        <span className="font-mono font-bold text-sm qhd:text-base 4k:text-lg text-primary dark:text-primary-light">
          {powerNo}
        </span>
        <ElectronicPowerBadge power={details} powerNumber={powerNo} agencyId={agencyId} size="xs" />
      </span>
    </td>
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      {isSaving && !details?.defendantName
        ? <ShimmerBar className="w-28 qhd:w-36" />
        : <span className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white">{details?.defendantName || '\u2014'}</span>
      }
    </td>
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-right">
      {isSaving && details?.bondAmount == null
        ? <ShimmerBar className="w-16 qhd:w-20 ml-auto" />
        : <span className="text-sm qhd:text-base 4k:text-lg font-bold text-gray-900 dark:text-white tabular-nums">{details?.bondAmount != null ? formatCurrencyWithSymbol(details.bondAmount) : '\u2014'}</span>
      }
    </td>
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      {isSaving && !validation
        ? <ShimmerBar className="w-16 qhd:w-20" />
        : <ValidationChip validation={validation} powerStatus={details?.status} statusName={details?.statusName} />
      }
    </td>
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      {isSaving && !validation
        ? <ShimmerBar className="w-14 qhd:w-16" />
        : <ProofCell powerNo={powerNo} validation={validation} proofData={proofData} hasServerProof={details?.hasProofUploaded} onUploadProof={onUploadProof} />
      }
    </td>
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 w-12">
      <button
        type="button"
        onClick={() => onRemove(powerNo)}
        className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-red-500 dark:hover:bg-red-600 transition-all duration-150"
        aria-label={`Remove ${powerNo}`}
      >
        <XMarkIcon className="h-3.5 w-3.5 qhd:h-4 qhd:w-4" />
      </button>
    </td>
  </tr>
);

// ============================================================================
// Auto-complete suggestion row
// ============================================================================

const SuggestionRow = ({ power, isHighlighted, onSelect, agencyId }) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault(); // Prevent input blur
      onSelect(power);
    }}
    className={`w-full px-3 py-2.5 qhd:px-4 qhd:py-3 flex items-center justify-between text-left transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 ${
      isHighlighted
        ? 'bg-primary/10 dark:bg-primary/20'
        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
    }`}
  >
    <div className="flex items-center gap-3 min-w-0">
      <span className="font-mono font-semibold text-sm qhd:text-base text-primary dark:text-primary-light shrink-0 inline-flex items-center gap-1.5">
        {power.powerNo}
        <ElectronicPowerBadge power={power} powerNumber={power.powerNo} agencyId={agencyId} size="xs" />
      </span>
      {power.defendantName && (
        <span className="text-sm qhd:text-base text-gray-600 dark:text-gray-400 truncate">
          {power.defendantName}
        </span>
      )}
    </div>
    <div className="flex items-center gap-3 shrink-0 ml-3">
      {power.status && (
        <span className="text-xs qhd:text-sm px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
          {power.statusName || STATUS_LABELS[power.status] || power.status}
        </span>
      )}
      {power.bondAmount != null && (
        <span className="text-sm qhd:text-base font-medium text-gray-900 dark:text-white tabular-nums">
          {formatCurrencyWithSymbol(power.bondAmount)}
        </span>
      )}
    </div>
  </button>
);

// ============================================================================
// Main Component
// ============================================================================

const DEBOUNCE_MS = 300;

const PowerNumberEntry = ({
  selectedPowerNos = new Set(),
  onSelectionChange,
  onAddUnverifiedPowers,
  onSaveAsDraft,
  agencyId,
  powerDetailsMap = new Map(),
  onPowerDetailsUpdate,
  // Inline validation & proof props
  clientValidationMap = new Map(),
  proofsByPower = new Map(),
  onUploadProof,
  onSubmitDischarge,
  isSubmitting = false,
  reason = '',
  onReasonChange,
  agentComments = '',
  onCommentsChange,
  savingPowerNos = new Set(),
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [inlineErrors, setInlineErrors] = useState([]);
  const [pendingTokens, setPendingTokens] = useState(() => new Set());
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Auto-complete state
  const [dropdownDismissed, setDropdownDismissed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Reason & Comments expanded state
  const [commentsExpanded, setCommentsExpanded] = useState(!!(reason || agentComments));

  // Clear duplicate notification after delay
  useEffect(() => {
    if (duplicateCount > 0) {
      const timer = setTimeout(() => setDuplicateCount(0), 2000);
      return () => clearTimeout(timer);
    }
  }, [duplicateCount]);

  // Inline validation errors persist until the user dismisses them with the
  // "Clear all" button — they are not auto-cleared on a timer, so the user has
  // time to review every rejected power.

  // ---------------------------------------------------------------
  // Debounced API search — sends prefix to the backend
  // ---------------------------------------------------------------
  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH || /[,;\t\n]/.test(trimmed)) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    const timer = setTimeout(() => {
      searchEligiblePowers(trimmed, agencyId)
        .then((result) => {
          if (!cancelled && result.success) {
            setSuggestions(result.data?.powers || []);
          }
        })
        .finally(() => {
          if (!cancelled) setIsSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inputValue, agencyId]);

  // Derived dropdown visibility
  const filteredSuggestions = suggestions.filter((p) => !selectedPowerNos.has(p.powerNo));
  const hasSuggestions = !dropdownDismissed && isFocused && filteredSuggestions.length > 0;

  // Position dropdown just below the input container
  const dropdownTop = (() => {
    if (!hasSuggestions || !containerRef.current || !wrapperRef.current) return 0;
    const containerRect = containerRef.current.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    return containerRect.bottom - wrapperRect.top + 4;
  })();

  // ---------------------------------------------------------------
  // Select a suggestion from the dropdown
  // ---------------------------------------------------------------
  const handleSelectSuggestion = useCallback(
    (power) => {
      const newSet = new Set(selectedPowerNos);
      if (newSet.has(power.powerNo)) {
        setDuplicateCount(1);
      } else {
        newSet.add(power.powerNo);

        // Store metadata BEFORE selection change so the queue sync can read it
        if (onPowerDetailsUpdate) {
          onPowerDetailsUpdate(power.powerNo, {
            defendantName: power.defendantName,
            bondAmount: power.bondAmount,
            status: power.status,
            state: power.state,
            type: power.type,
            isForfeiture: power.isForfeiture,
            batchClosed: power.batchClosed,
          });
        }

        onSelectionChange(newSet);
      }

      setInputValue('');
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    },
    [selectedPowerNos, onSelectionChange, onPowerDetailsUpdate],
  );

  // ---------------------------------------------------------------
  // Parse text into tokens and add to the selection set
  //
  // Known tokens (matched in the typeahead suggestions) are added
  // optimistically — the hook rolls back if the backend rejects.
  // Unknown tokens (typed/pasted, not yet validated) go through the
  // pessimistic path: the backend confirms validity before anything
  // appears in the queue, so random strings never flash in the UI.
  // ---------------------------------------------------------------
  const parseAndAddNumbers = useCallback(
    (text) => {
      const tokens = text
        .split(/[,\s;]+/)
        .map((t) => t.trim().toUpperCase())
        .filter((t) => t.length > 0);

      if (tokens.length === 0) return;

      const suggestionsMap = new Map(suggestions.map((p) => [p.powerNo.toUpperCase(), p]));

      const newSet = new Set(selectedPowerNos);
      const unknownTokens = [];
      let dupes = 0;

      for (const token of tokens) {
        if (newSet.has(token)) {
          dupes++;
          continue;
        }

        const matched = suggestionsMap.get(token);
        if (matched) {
          newSet.add(token);
          if (onPowerDetailsUpdate) {
            onPowerDetailsUpdate(matched.powerNo, {
              defendantName: matched.defendantName,
              bondAmount: matched.bondAmount,
              status: matched.status,
              state: matched.state,
              type: matched.type,
              isForfeiture: matched.isForfeiture,
              batchClosed: matched.batchClosed,
            });
          }
        } else {
          unknownTokens.push(token);
        }
      }

      if (dupes > 0) setDuplicateCount(dupes);

      // Commit known-valid additions immediately (optimistic)
      if (newSet.size !== selectedPowerNos.size) {
        onSelectionChange(newSet);
      }

      // Route unknown tokens through the pessimistic add — they only enter
      // the queue after the backend confirms validity. Show pending chips
      // with a spinner so the user knows the backend is checking them.
      if (unknownTokens.length > 0 && onAddUnverifiedPowers) {
        setPendingTokens((prev) => {
          const next = new Set(prev);
          for (const t of unknownTokens) next.add(t);
          return next;
        });
        onAddUnverifiedPowers(unknownTokens)
          .then((result) => {
            if (result?.invalid?.length > 0) {
              setInlineErrors(result.invalid);
            }
          })
          .finally(() => {
            setPendingTokens((prev) => {
              const next = new Set(prev);
              for (const t of unknownTokens) next.delete(t);
              return next;
            });
          });
      }

      setInputValue('');
      setHighlightedIndex(-1);
    },
    [selectedPowerNos, onSelectionChange, suggestions, onPowerDetailsUpdate, onAddUnverifiedPowers],
  );

  // ---------------------------------------------------------------
  // Keyboard handler
  // ---------------------------------------------------------------
  const handleKeyDown = useCallback(
    (e) => {
      // Compute filtered suggestions inside callback for fresh values
      const currentSuggestions = suggestions.filter((p) => !selectedPowerNos.has(p.powerNo));
      const hasDropdown = currentSuggestions.length > 0 && !dropdownDismissed;

      // Arrow navigation in suggestions
      if (hasDropdown) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < currentSuggestions.length - 1 ? prev + 1 : 0,
          );
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : currentSuggestions.length - 1,
          );
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setDropdownDismissed(true);
          setHighlightedIndex(-1);
          return;
        }
        // Enter/Tab with dropdown open → select highlighted or first item
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
          handleSelectSuggestion(currentSuggestions[idx]);
          return;
        }
      }

      // Commit typed text on Enter/Tab only — comma is treated as a separator
      // inside the input, not a commit trigger, so pasting "A,B,C" stays
      // visible until the user explicitly commits with Enter or blurs.
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (inputValue.trim()) {
          parseAndAddNumbers(inputValue);
        }
      }

      if (e.key === 'Backspace' && !inputValue && selectedPowerNos.size > 0) {
        const arr = Array.from(selectedPowerNos);
        const newSet = new Set(arr.slice(0, -1));
        onSelectionChange(newSet);
      }
    },
    [inputValue, parseAndAddNumbers, selectedPowerNos, onSelectionChange,
     dropdownDismissed, suggestions, highlightedIndex, handleSelectSuggestion],
  );

  // ---------------------------------------------------------------
  // Paste handler — land the pasted text into the input so the user can
  // see it before committing. Newlines/tabs/semicolons from spreadsheets
  // get normalized to commas so the content stays readable. Validation
  // runs on Enter or blur, not on paste.
  // ---------------------------------------------------------------
  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text') || '';
      const normalized = pasted
        .replace(/[\n\t;]+/g, ', ')
        .replace(/^[,\s]+|[,\s]+$/g, '');
      if (!normalized) return;
      setInputValue((prev) => {
        const trimmed = prev.trim();
        return trimmed ? `${trimmed}, ${normalized}` : normalized;
      });
    },
    [],
  );

  // ---------------------------------------------------------------
  // Blur — commit remaining text
  // ---------------------------------------------------------------
  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      parseAndAddNumbers(inputValue);
    }
    setIsFocused(false);
    setHighlightedIndex(-1);
  }, [inputValue, parseAndAddNumbers]);

  // ---------------------------------------------------------------
  // Remove / Clear
  // ---------------------------------------------------------------
  const handleRemove = useCallback(
    (powerNo) => {
      const newSet = new Set(selectedPowerNos);
      newSet.delete(powerNo);
      onSelectionChange(newSet);
    },
    [selectedPowerNos, onSelectionChange],
  );

  const handleClearAll = useCallback(() => {
    onSelectionChange(new Set());
    setInputValue('');
    inputRef.current?.focus();
  }, [onSelectionChange]);

  const count = selectedPowerNos.size;
  const powerNosArray = Array.from(selectedPowerNos);

  // Pagination for selected powers list
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  // Reset to last valid page when items are removed or page size changes
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Navigate when new items are added:
  //  - Single add (typeahead): jump to the last page so the new row is visible.
  //  - Bulk add (paste/scan of many): go to page 1 so the user reviews from the
  //    top rather than landing deep in a long list.
  const prevCountRef = useRef(0);
  useEffect(() => {
    const added = count - prevCountRef.current;
    if (added > 0) {
      setCurrentPage(added === 1 ? Math.ceil(count / pageSize) : 1);
    }
    prevCountRef.current = count;
  }, [count, pageSize]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const pagedPowers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return powerNosArray.slice(start, start + pageSize);
  }, [powerNosArray, currentPage, pageSize]);

  // Total bond amount across all selected powers
  const totalBondAmount = useMemo(() => {
    let sum = 0;
    for (const powerNo of selectedPowerNos) {
      const details = powerDetailsMap.get(powerNo);
      if (details?.bondAmount != null) sum += details.bondAmount;
    }
    return sum;
  }, [selectedPowerNos, powerDetailsMap]);

  // Validation summary counts
  const validationSummary = useMemo(() => {
    let ready = 0;
    let needsProof = 0;
    let blocked = 0;
    for (const [, v] of clientValidationMap) {
      if (v.status === 'ready') ready++;
      else if (v.status === 'needsProof') needsProof++;
      else if (v.status === 'blocked') blocked++;
    }
    return { ready, needsProof, blocked };
  }, [clientValidationMap]);

  // True when any power requires proof but none has been uploaded
  const hasMissingProof = useMemo(() => {
    for (const [powerNo, v] of clientValidationMap) {
      if (v.status === 'needsProof') {
        const hasClientProof = !!proofsByPower.get(powerNo)?.fileId;
        const hasServerProof = !!powerDetailsMap.get(powerNo)?.hasProofUploaded;
        if (!hasClientProof && !hasServerProof) return true;
      }
    }
    return false;
  }, [clientValidationMap, proofsByPower, powerDetailsMap]);

  return (
    <div className="flex flex-col gap-5">
      {/* Header area */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
        <div>
          <h3 className="text-sm sm:text-base qhd:text-lg 4k:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HashtagIcon className="h-5 w-5 qhd:h-6 qhd:w-6 text-primary" />
            Enter Power Numbers
          </h3>
          <p className="mt-1 text-xs sm:text-sm qhd:text-base 4k:text-lg text-gray-500 dark:text-gray-400">
            Type, paste, or scan power numbers to discharge.
            Separate multiple entries with commas, spaces, or new lines.
          </p>
        </div>
        {count > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            className="shrink-0 self-end sm:self-auto text-sm qhd:text-base font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Input container + auto-complete dropdown wrapper */}
      <div ref={wrapperRef} className="relative">
        <div
          ref={containerRef}
          className={`rounded-xl border-2 bg-white dark:bg-gray-700/50 transition-all duration-200 ${
            isFocused
              ? 'border-primary dark:border-primary-light shadow-lg shadow-primary/5 dark:shadow-primary/10'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
          }`}
          onClick={() => inputRef.current?.focus()}
        >
          {/* Input field */}
          <div className="px-3 py-3 qhd:px-4 qhd:py-3.5">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); setDropdownDismissed(false); }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              placeholder={
                count === 0
                  ? 'Type power numbers here...'
                  : 'Add more powers...'
              }
              className="w-full bg-transparent border-none outline-none text-base sm:text-sm qhd:text-base text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {/* Bottom bar with count + paste hint */}
          <div className="flex items-center justify-between px-3 py-2 qhd:px-4 qhd:py-2.5 border-t border-gray-100 dark:border-gray-600/50 bg-gray-50/50 dark:bg-gray-800/30 rounded-b-xl">
            <div className="flex items-center gap-3">
              {count > 0 && (
                <span className="text-xs qhd:text-sm font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                  {count} power{count !== 1 ? 's' : ''} selected
                </span>
              )}

              {/* Duplicate notification */}
              {duplicateCount > 0 && (
                <span className="text-xs qhd:text-sm text-amber-600 dark:text-amber-400">
                  {duplicateCount} duplicate{duplicateCount > 1 ? 's' : ''} skipped
                </span>
              )}
            </div>

            {/* Searching / Paste hint */}
            {isSearching ? (
              <span className="flex items-center gap-1.5 text-xs qhd:text-sm text-gray-400 dark:text-gray-500">
                <OathSpinner size="sm" />
                Searching...
              </span>
            ) : count === 0 ? (
              <span className="hidden sm:flex items-center gap-1.5 text-xs qhd:text-sm text-gray-400 dark:text-gray-500">
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                Paste from spreadsheet
              </span>
            ) : null}
          </div>
        </div>

        {/* Pending-validation banner — shown while the backend verifies tokens
            the user just committed via Enter/blur. A compact count summary scales
            cleanly for large pastes; the first few tokens are previewed as chips
            with a "+N more" overflow indicator so small batches stay informative. */}
        {pendingTokens.size > 0 && (() => {
          const tokens = Array.from(pendingTokens);
          const MAX_PREVIEW = 8;
          const previewed = tokens.slice(0, MAX_PREVIEW);
          const overflow = tokens.length - previewed.length;
          return (
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/70 dark:bg-gray-800/40 px-3 py-2">
              <span className="flex items-center gap-1.5 text-xs qhd:text-sm font-medium text-gray-600 dark:text-gray-300">
                <OathSpinner size="sm" />
                Validating {tokens.length} power{tokens.length !== 1 ? 's' : ''}&hellip;
              </span>
              {previewed.map((token) => (
                <span
                  key={token}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs qhd:text-sm font-mono font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                >
                  {token}
                </span>
              ))}
              {overflow > 0 && (
                <span className="text-xs qhd:text-sm text-gray-400 dark:text-gray-500 tabular-nums">
                  +{overflow} more
                </span>
              )}
            </div>
          );
        })()}

        {/* Inline validation errors for unknown tokens rejected by backend */}
        {inlineErrors.length > 0 && (
          <div className="mt-2 rounded-lg border border-red-200 dark:border-red-800/60 bg-red-50/70 dark:bg-red-900/10 px-3 py-2">
            <div className="flex items-start justify-between gap-2">
              <ul className="space-y-0.5 text-xs qhd:text-sm text-red-700 dark:text-red-400">
                {inlineErrors.map(({ powerNo, reason }) => (
                  <li key={powerNo}>
                    <span className="font-mono font-semibold">{powerNo}</span>
                    <span className="mx-1.5 text-red-400 dark:text-red-500">—</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setInlineErrors([])}
                className="flex items-center gap-1 text-xs qhd:text-sm font-medium text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 shrink-0"
                aria-label="Clear all errors"
              >
                <XMarkIcon className="h-4 w-4" />
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Auto-complete dropdown */}
        {hasSuggestions && (
          <div
            className="absolute left-0 right-0 z-50 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-xl max-h-[280px] sm:max-h-[300px] overflow-y-auto"
            style={{ top: `${dropdownTop}px` }}
          >
            {filteredSuggestions.slice(0, MAX_SUGGESTIONS).map((power, idx) => (
              <SuggestionRow
                key={power.powerNo}
                power={power}
                isHighlighted={idx === highlightedIndex}
                onSelect={handleSelectSuggestion}
                agencyId={agencyId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Selected powers list */}
      {count > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          {/* Desktop table */}
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                  <th scope="col" className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Power #
                  </th>
                  <th scope="col" className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Defendant
                  </th>
                  <th scope="col" className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-right text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Amount
                  </th>
                  <th scope="col" className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Proof
                  </th>
                  <th scope="col" className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 w-12" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {pagedPowers.map((powerNo, index) => (
                  <SelectedPowerRow
                    key={powerNo}
                    powerNo={powerNo}
                    details={powerDetailsMap.get(powerNo)}
                    validation={clientValidationMap.get(powerNo)}
                    proofData={proofsByPower.get(powerNo)}
                    onRemove={handleRemove}
                    onUploadProof={onUploadProof}
                    isEven={index % 2 === 1}
                    isSaving={savingPowerNos.has(powerNo)}
                    agencyId={agencyId}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-200 dark:border-gray-600">
                  <td colSpan={2} className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-right text-sm qhd:text-base 4k:text-lg font-semibold text-gray-600 dark:text-gray-300">
                    Total Liability
                  </td>
                  <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-right text-sm qhd:text-base 4k:text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatCurrencyWithSymbol(totalBondAmount)}
                  </td>
                  <td colSpan={3} className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
                    {clientValidationMap.size > 0 && (
                      <span className="text-xs qhd:text-sm 4k:text-base text-gray-500 dark:text-gray-400">
                        <span className="text-green-600 dark:text-green-400 font-semibold">{validationSummary.ready}</span> ready
                        {validationSummary.needsProof > 0 && (
                          <>, <span className="text-orange-600 dark:text-orange-400 font-semibold">{validationSummary.needsProof}</span> need proof</>
                        )}
                        {validationSummary.blocked > 0 && (
                          <>, <span className="text-red-600 dark:text-red-400 font-semibold">{validationSummary.blocked}</span> blocked</>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile card layout */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            {pagedPowers.map((powerNo) => {
              const details = powerDetailsMap.get(powerNo);
              const validation = clientValidationMap.get(powerNo);
              const proofData = proofsByPower.get(powerNo);
              const isSaving = savingPowerNos.has(powerNo);
              return (
                <div key={powerNo} className="px-3 py-3.5 sm:px-3 sm:py-3 space-y-3 sm:space-y-2">
                  {/* Row 1: Power # + amount + remove */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono font-bold text-sm text-primary dark:text-primary-light">
                      {powerNo}
                    </span>
                    <div className="flex items-center gap-2">
                      {isSaving && details?.bondAmount == null
                        ? <ShimmerBar className="w-16" />
                        : details?.bondAmount != null && (
                          <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                            {formatCurrencyWithSymbol(details.bondAmount)}
                          </span>
                        )
                      }
                      <button
                        type="button"
                        onClick={() => handleRemove(powerNo)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-red-500 dark:hover:bg-red-600 transition-all"
                        aria-label={`Remove ${powerNo}`}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {/* Row 2: Defendant */}
                  {(isSaving && !details?.defendantName) ? (
                    <ShimmerBar className="w-32" />
                  ) : details?.defendantName ? (
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{details.defendantName}</p>
                  ) : null}
                  {/* Row 3: Status + Proof */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {isSaving && !validation
                      ? <ShimmerBar className="w-16" />
                      : <ValidationChip validation={validation} powerStatus={details?.status} statusName={details?.statusName} />
                    }
                    {isSaving && !validation
                      ? <ShimmerBar className="w-14" />
                      : <ProofCell powerNo={powerNo} validation={validation} proofData={proofData} hasServerProof={details?.hasProofUploaded} onUploadProof={onUploadProof} />
                    }
                  </div>
                </div>
              );
            })}
            {/* Mobile footer — total */}
            <div className="px-3 py-3 bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">Total Liability</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{formatCurrencyWithSymbol(totalBondAmount)}</span>
              </div>
              {clientValidationMap.size > 0 && (
                <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span className="text-green-600 dark:text-green-400 font-semibold">{validationSummary.ready}</span> ready
                  {validationSummary.needsProof > 0 && (
                    <>, <span className="text-orange-600 dark:text-orange-400 font-semibold">{validationSummary.needsProof}</span> need proof</>
                  )}
                  {validationSummary.blocked > 0 && (
                    <>, <span className="text-red-600 dark:text-red-400 font-semibold">{validationSummary.blocked}</span> blocked</>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Pagination controls — shown whenever the list is large enough to be
              paginated (count exceeds the smallest page size), not only when it's
              currently split across pages. This keeps the page-size selector
              reachable after the user picks a size larger than the record count. */}
          {count > PAGE_SIZE_OPTIONS[0] && (
            <OathPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={count}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
              label="powers"
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          )}
        </div>
      )}

      {/* Reason & Comments — collapsible */}
      {count > 0 && onReasonChange && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            type="button"
            onClick={() => setCommentsExpanded((prev) => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 qhd:px-6 qhd:py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ChatBubbleLeftEllipsisIcon className="h-5 w-5 qhd:h-6 qhd:w-6 text-gray-400 dark:text-gray-500" />
              <span className="text-sm qhd:text-base font-semibold text-gray-700 dark:text-gray-300">
                Reason & Comments
              </span>
              <span className="text-xs qhd:text-sm text-gray-400 dark:text-gray-500">(optional)</span>
              {!commentsExpanded && (reason || agentComments) && (
                <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                  Has content
                </span>
              )}
            </div>
            <ChevronDownIcon className={`h-5 w-5 qhd:h-6 qhd:w-6 text-gray-400 transition-transform duration-200 ${commentsExpanded ? 'rotate-180' : ''}`} />
          </button>
          <div className={`grid transition-all duration-200 ease-in-out ${commentsExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
            <div className="overflow-hidden">
              <div className="px-4 pb-4 qhd:px-6 qhd:pb-6 space-y-4 qhd:space-y-5 border-t border-gray-100 dark:border-gray-700/50 pt-4">
                <OathInput
                  label="Reason for discharge"
                  value={reason}
                  onChange={onReasonChange}
                  placeholder="e.g., Court dismissed, Charges dropped"
                  disabled={isSubmitting}
                />
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Additional comments
                  </label>
                  <textarea
                    value={agentComments}
                    onChange={(e) => onCommentsChange(e.target.value)}
                    placeholder="Any additional notes for this discharge request..."
                    disabled={isSubmitting}
                    rows={3}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 border rounded-xl text-sm sm:text-base bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 border-gray-300 dark:border-gray-600 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        {onSaveAsDraft && (
          <OathButton
            variant="outline"
            size="lg"
            disabled={count === 0}
            onClick={onSaveAsDraft}
            className="w-full sm:w-auto"
          >
            Save as Draft
          </OathButton>
        )}
        <Tooltip
          title={hasMissingProof ? 'Upload proof for all required powers before submitting' : ''}
          placement="top"
          arrow
        >
          <span className="w-full sm:w-auto">
            <OathButton
              size="lg"
              disabled={count === 0 || hasMissingProof}
              onClick={onSubmitDischarge}
              loading={isSubmitting}
              className="w-full sm:w-auto"
            >
              Submit Discharge
              {count > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 qhd:min-w-[1.5rem] qhd:h-6 px-1.5 rounded-full bg-white/20 text-xs qhd:text-sm font-bold tabular-nums">
                  {count}
                </span>
              )}
            </OathButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default PowerNumberEntry;
